<template>
  <div v-if="model" class="store-page">
    <div v-if="!loaded" style="padding-top: 100vh" />

    <main id="main" v-if="loaded" class="-p-relative" :class="themeClass">
        <section class="store-page-header">
            <div class="store-page-title" :class="{'-has-image': mainImage}">
                <div class="grid">
                    <div class="g-12">
                        <h1 class="font-responsive-larger font-responsive-lg-xl m-b-0">{{ title }}</h1>
                        <p class="font-responsive-larger font-responsive-lg-xl -thin  m-b-0">{{ street }}</p>
                    </div>
                </div>
            </div>
            <div class="overlay-image -bottom" />
            <SliderFormatData
            v-if="mainImage"
            :model="mainImage"
            :sizes="'100vw'"
            :proportions="proportion"
            >
            <template v-slot="{ proportion, slides }">
                <MediaCarousel
                :slides="slides"
                :proportion="proportion"
                :tooltip="false"
                :disableLazy="true"
                :navColorOverride="Globals.colors.white"
                :navPlacement="$mqAliases.desktop ? '36px' : '18px'"
                :navLargeHitAreas="true"
                @ready="$_removePageLoader()"
                />
            </template>
            </SliderFormatData>
        </section>

      <section class="store-page-wrapper grid  m-t-18 m-t-md-36">
        <section class="store-page-left-col g-12 g-md-4 m-b-18">
            <div v-if="storeData" class="panelcard font-responsive-body" :class="{'p-b-0': !expandDrawer}">
                <StoreOpeningHours
                    v-if="storeData.OpeningHours && storeData.OpeningHours.length"
                    :model="storeData.OpeningHours"
                    class="m-t-12"
                />
                <div  class="d-flex flex-wrap -gray-light m-t-18 font-responsive-small">
                    <div v-if="storeData.HasReserveInStore" class="m-r-9 -nowrap">
                    {{ insertString("FindStore", "HasReserveInStore") }}
                    </div>
                    <div v-if="storeData.HasPickupInStore" class="m-r-9 -nowrap">
                    {{ insertString("FindStore", "HasPickupInStore") }}
                    </div>
                    <div v-if="storeData.HasOrderFromStore" class="m-r-9 -nowrap">
                    {{ insertString("FindStore", "HasOrderFromStore") }}
                    </div>
                    <div v-if="storeData.HasTwoHourDelivery" class="m-r-9 -nowrap">
                    {{ insertString("FindStore", "HasTwoHourDelivery") }}
                    </div>
                    <div>&nbsp;</div>
                </div>
                <hr class="d-none d-md-block" />
                <div class="store-page-mobile-drawer-btn d-md-none" @click="expandDrawer = !expandDrawer">
                    <span>Contact and map</span>
                    <Chevron :color="Globals.colors.white" :direction="!expandDrawer ? 'down' : 'up'" />
                </div>
                <TransitionExpand>
                    <div v-if="expandDrawer" class="store-page-mobile-drawer" >
                        <h3 class="font-responsive-body m-t-18" >How to contact us</h3>
                        <div v-if="storeData.Email" class="d-flex align-items-center m-b-9">
                            <SvgComponent :icon="{ name: 'Email' }" />
                            <a
                            :href="`mailto:${storeData.Email}`"
                            class="-clickable -underline-hover m-l-9"
                            >{{ insertString("Contact", "Email") }}</a
                            >
                        </div>
                        <div v-if="storeData && storeData.Phone" class="d-flex no-font-spacing">
                            <SvgComponent
                                :icon="{
                                    name: 'Chat',
                                    color: Globals.colors.black,
                                }"
                                />
                            <a
                            :href="`tel:${storeData.Phone}`"
                            class="-clickable -underline-hover m-l-9"
                            :aria-label="`Norway ${storeData.Phone}`"
                            >{{ storeData.Phone }}</a
                            >
                        </div>

                        <hr  />

                        <div v-if="storeData.VisitorAddress" class="m-b-18">
                            <div class="map" ref="googleMap" id="map"/>
                            <a class="font-responsive-small -clickable -ellipsis" target="_blank" :href="`https://www.google.com/maps?saddr=My+Location&daddr=${storeData.VisitorAddress.Latitude},${storeData.VisitorAddress.Longitude}`"
                                >{{ insertString("FindStore", "CalculateRoute") }}
                            </a>
                          <div class="font responsive-small">{{storeData.VisitorAddress.Line1 }}, {{ storeData.VisitorAddress.PostCode }} {{ storeData.VisitorAddress.City }}</div>
                        </div>
                    </div>
            </TransitionExpand>

            </div>
        </section>

        <section class="store-page-right-col g-12 g-md-8">
          <div class="lead-text font-responsive-body" v-html="model.Lead" />
          <ContentArea
            v-if="contentAreaData.length"
            :model="contentAreaData"
            :inline="true"
          />

        </section>

      </section>
      <section class="store-page-common-content">
        <hr />
        <EventsBlock :model="eventData" :darkMode="true" class="p-b-0 m-b-18" />
        <hr />
        <ProductsInActionBlock v-if="imageGalleryData" :model="imageGalleryData"/>
    </section>
    </main>

  </div>
</template>

<script>
import Api from '@/Scripts/api/api';
import UrlHelper from '@/Scripts/lib/helper/urlHelper.js';
// import FocalImage from '@/Scripts/components/media/Image/FocalImage.vue';
import SliderFormatData from '@/Scripts/components/shared/GenericSlider/Assets/SliderFormatData.vue';
import { PROPORTIONS, MAP_DEFAULT_STYLES } from '@/Scripts/constants';
import StoreOpeningHours from '@/Scripts/components/pages/FindStore/StoreOpeningHours.vue';
import SvgComponent from '@/Scripts/components/shared/svg/Svg.vue';

import themeHelper from '@/Scripts/lib/helper/themeHelper.js';
import pageLoader from '@/Scripts/lib/helper/pageLoader.js';
import { mapState } from 'vuex';
import { Loader } from '@googlemaps/js-api-loader';
import siteInfo from '@/Scripts/lib/helper/siteInfo.js';
import dateMixin from '@/Scripts/lib/mixins/dateMixin.js';
import { getOpenToday } from '@/Scripts/lib/helper/storeHelper.js';
import Chevron from '@/Scripts/components/shared/svg/Chevron/Chevron.vue';

const MediaCarousel = () => import('@/Scripts/components/media/MediaCarousel/MediaCarousel.vue');
const TransitionExpand = () => import(/* webpackChunkName: "TransitionExpand" */ '@/Scripts/components/shared/Transitions/TransitionExpand.vue');
const EventsBlock = () => import('@/Scripts/components/blocks/EventsBlock.vue');
const ContentArea = () => import(/* webpackChunkName: "ContentArea" */ '@/Scripts/components/shared/ContentArea/ContentArea.vue');
const ProductsInActionBlock = () => import('@/Scripts/components/blocks/ProductsInActionBlock.vue');
let map;

const defaultZoom = 14;

export default {
  name: 'StorePage',
  mixins: [pageLoader, themeHelper, dateMixin, siteInfo],
  components: {
    // FocalImage,
    ContentArea,
    EventsBlock,
    ProductsInActionBlock,
    MediaCarousel,
    SliderFormatData,
    StoreOpeningHours,
    SvgComponent,
    TransitionExpand,
    Chevron

  },
  data () {
    return {
      loaded: false,
      model: {},
      imageReady: false,
      bannerProportion: PROPORTIONS.SMALL,
      viewSignUpModal: false,
      registrationId: null,
      mounted: false,
      storeData: null,
      mainImage: null,
      imageGalleryMedia: null,
      proportion: PROPORTIONS.MEDIUM,
      expandDrawer: true,
      mapIsReady: false
    };
  },
  watch: {
    readyToMountMap (val) {
      if (val) {
        setTimeout(() => {
          this.initMap(this.storeMapPosition);
        }, 330);
      }
    }
  },
  computed: {
    ...mapState({
      googleApiKey: state => state.siteInfo.googleApiKey,
      isLoyalty: state => state.appContext.isLoyalty
    }),
    readyToMountMap () {
      return this.mounted && this.hasGeoData && this.expandDrawer && !this.mapIsReady;
    },
    contentAreaData () {
      if (!this.model || !this.model.Content) {
        return [];
      }
      // this.model.Content.forEach(i => {
      //   if (i.__type__ === 'MediaBlock') {
      //     i.__type__ = 'PersonBlock';
      //   }
      // });
      return this.model.Content.filter(b => !['ImageCollectorMaxBlock', 'ImageCollectorSliderBlock', 'ProductsInActionBlock', 'EventsBlock'].includes(b.__type__));
    },
    title () {
      if (this.storeData) {
        return this.storeData.Name;
      }
      return this.model.TitleBlock.Title;
    },
    street () {
      if (this.storeData && this.storeData.VisitorAddress) {
        return this.storeData.VisitorAddress.Line1;
      }
      return '';
    },
    eventData () {
      return {
        Title: 'Upcoming events',
        Activity: 0,
        Store: this.model.StorePageKey,
        Link: this.getLink('Events'),
        LinkText: 'View all',
        ColorOptions: {
          BackgroundColor: null,
          FontColor: null,
          GradientTo: null
        }
      };
    },
    imageGalleryData () {
      if (this.imageGalleryMedia) {
        return {
          Title: 'Gallery',
          ColorOptions: {
            BackgroundColor: this.Globals.colors.black
          },
          ImageCollectorMaxBlock: {
            Media: this.imageGalleryMedia
          }
        };
      }
      return null;
    },
    lang () {
      return {
        'en-GB': 'en',
        'nb-NO': 'no',
        'sv-SE': 'sv',
        'fr-FR': 'fr',
        'de-DE': 'de'
      }[document.documentElement.lang];
    },

    isMobile () {
      return !this.$mqAliases.md && !this.$mqAliases.desktop;
    },
    storeMapPosition () {
      return {
        lat: this.hasGeoData ? this.storeData.VisitorAddress.Latitude : 0,
        lng: this.hasGeoData ? this.storeData.VisitorAddress.Longitude : 0
      };
    },
    hasGeoData () {
      return this.storeData && this.storeData.VisitorAddress && this.storeData.VisitorAddress.Latitude && this.storeData.VisitorAddress.Longitude;
    },
    overlay () {
      return this.model.GradientOverlay;
    },
    themeClass () {
      if (typeof this.themeName !== 'undefined') {
        if (this.themeName === 'black') {
          return '-black-theme';
        } else if (this.themeName === 'blackest') {
          return '-black-theme -blackest-theme';
        }
      }
      return '';
    },

    isReady () {
      return this.mounted && this.loaded && this.googleApiKey;
    },
    openToday () {
      if (this.storeData && this.storeData.OpeningHours && this.storeData.OpeningHours.length) {
        return getOpenToday(this.storeData.OpeningHours);
      }
      return null;
    }
  },
  methods: {
    async getStoreDataById (id, type) { // type: details | gallery
      const response = await Api.getContentByFriendlyUrl(`/api/store/${type}?id=${id}`);
      if (response && response.data) {
        return response.data;
      }
      return null;
    },
    showMap () {
      this.expanded = !this.expanded;
      this.viewMap = !this.viewMap;
    },

    createMarker () {
      const icon = document.createElement('img');
      icon.src = require('@/Assets/markers/pin-black.png');
      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: this.storeMapPosition,
        map: map,
        content: icon
      });
      return marker;
    },
    initMap (center) {
      const loader = new Loader({
        apiKey: this.googleApiKey,
        version: 'weekly',
        libraries: ['places', 'marker']
      });
      loader.load().then(() => {
        const styledMapType = new google.maps.StyledMapType(MAP_DEFAULT_STYLES, { name: 'Map' });
        map = new google.maps.Map(this.$refs.googleMap, {
          center: center,
          zoom: defaultZoom,
          mapId: 'DEMO_MAP_ID',
          minZoom: 3,
          maxZoom: 18,
          panControl: false,
          zoomControl: true,
          fullscreenControl: false,
          mapTypeControl: false,
          mapTypeControlOptions: {
            position: 10,
            mapTypeIds: ['styled_map', 'satellite']
          },
          streetViewControl: false,
          gestureHandling: 'greedy'
        }
        );
        this.createMarker();
        map.mapTypes.set('styled_map', styledMapType);
        map.setMapTypeId('styled_map');
        this.mapIsReady = true;
      }).catch(e => {
        console.error(e);
      });
    }

  },

  mounted () {
    this.expandDrawer = !this.isMobile;
    this.$nextTick(() => {
      this.mounted = true;
    });
  },
  async created () {
    this.setTheme('black');
    document.body.style.background = this.Globals.colors.black;
    const response = await Api.getContentByFriendlyUrl(UrlHelper.getCurrentUrlWithAction('json'));
    if (response && response.data) {
      this.model = response.data;
      const storeData = await this.getStoreDataById(this.model.StorePageKey, 'details');
      if (storeData) {
        this.storeData = storeData.store;
        this.mainImage = storeData.image;
      }
      const gallerydata = await this.getStoreDataById(this.model.StorePageKey, 'gallery');
      if (gallerydata) {
        this.imageGalleryMedia = gallerydata.Media;
      }
    }
    this.loaded = true;
  }

};
</script>
<style lang="scss" src="@/scss/components/components.panelcard.scss"></style>
<style lang="scss" src="@/scss/components/components.overlay-image.scss"></style>
<style lang="scss">
@import "@/scss/defaults.scss";
.store-page {
    color: $white;
    background-color: $black;
     hr {
        display: block;
        height: 1px;
        border: 0;
        border-top: 1px solid $gray;
        margin: 18px 0 12px 0;
    }
    &-header {
        position: relative;
    }
    &-title.-has-image {
        position: absolute;
        width: 100%;
        bottom: 36px;
        z-index: 12;
        color: $white;
    }
    &-mobile-drawer-btn {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 60px;
        margin-left: -18px;
        margin-right: -18px;
        margin-top: 18px;
        margin-bottom: 0;
        padding: 0 18px;
        border-top: 1px solid $gray;
        border-bottom: 1px solid $gray;
    }
    &-common-content {
        hr {
            border-color: $white;
            margin: 0;
        }
    }
    .panelcard {
        color: $white;
        background-color: #2c2f32;
        a {
            color: $white;
        }
        path:last-child {
            stroke: $white;
        }
    }

    .events-row .row-header {
        padding-top: 18px !important;
        padding-bottom: 18px !important;
    }

     .products-in-action-block .row-header {
        padding-top: 18px !important;
    }

  .-divider {
        margin: 15px 0;
        border-bottom: 1px solid $gray-light;
    }
  .map {
    border: 1px solid $gray;
    border-radius: 6px;
    padding: 12px;
    transition: opacity 0.33s;
  }
  #map {
    min-height: 290px;
    max-height: calc(100vh - 200px);
    min-width: 100%;
    max-width: 100%;
    *:focus {
    outline: none !important;
    }
  }
  .block-wrapper {
    > div {
      margin-bottom: 24px;
    }
  }
  // .content-text-block * {
  //   grid-column: 1 !important;
  // }
  .overlay-text {
    position: absolute;
    z-index: 10001;
    // pointer-events: none;
  }
  &-banner {
    padding-bottom: 80%;
    img {
      height: 0;
    }
    @include media-breakpoint-up(md) {
      padding-bottom: 25%;
    }
    &.-ready {
      padding-bottom: 0;
      img {
        height: auto;
      }
    }
  }
  .overlay-image {
    z-index: 10;
  }

}
</style>
